import Component from '@glimmer/component';
import { modifier } from 'ember-modifier';
import { service } from '@ember/service';
import { tracked } from '@glimmer/tracking';
import { t } from 'ember-intl';
import Button from 'tio-ui/components/button';
import ConfirmationDialog from 'tio-ui/components/confirmation-dialog';
import { PhEye } from 'ember-phosphor-icons';
import type ImpersonationService from 'tio-employee/services/impersonation';
import type SessionContextService from 'tio-employee/services/session-context';
import type SessionService from 'tio-employee/services/session';
import type IntlService from 'ember-intl/services/intl';
import type RouterService from '@ember/routing/router-service';

// Deliberately not the app's own warning palette: these screens get screenshotted
// into tickets, and a partner must never mistake one for their own product.
export default class ImpersonationBanner extends Component {
  @service declare impersonation: ImpersonationService;
  @service declare sessionContext: SessionContextService;
  @service declare session: SessionService;
  @service declare intl: IntlService;
  @service declare router: RouterService;

  @tracked timeRemaining = '';

  /** Ember owns the interval's teardown, so a torn-down banner cannot tick on. */
  countdown = modifier(() => {
    this.#tick();
    const id = setInterval(() => this.#tick(), 1000);
    return () => clearInterval(id);
  });

  #tick() {
    // The wall, not the access token: the token is renewed every few minutes, so
    // a countdown to it resets and says nothing about the session's remaining time.
    const deadline = this.impersonation.expiresAt ?? this.session.expireAt;
    if (!deadline) {
      this.timeRemaining = '';
      return;
    }
    const diff = Math.max(0, Math.floor((deadline - Date.now()) / 1000));
    const mins = Math.floor(diff / 60);
    const secs = diff % 60;
    this.timeRemaining = `${mins}:${String(secs).padStart(2, '0')}`;
  }

  get personName() {
    return this.sessionContext.person?.fullName || this.intl.t('impersonation.unnamed_person');
  }

  get partnerName() {
    return this.sessionContext.currentCompany?.name ?? '';
  }

  get roleLabel() {
    const claim = this.sessionContext.rolesForCurrentCompany[0]?.relationshipType;
    if (!claim) return '';

    const key = `people.jurisdictions.${claim.split('.').pop()}`;
    return this.intl.exists(key) ? this.intl.t(key) : claim;
  }

  endSession = () => {
    this.router.transitionTo('logout');
  };

  <template>
    {{#if this.impersonation.isImpersonated}}
      <div class="tio-view-as-frame" data-test-impersonation-banner {{this.countdown}}>
        <div class="tio-view-as-bar">
          <PhEye @size={{18}} @weight="fill" class="shrink-0" />

          <div class="tio-view-as-identity">
            <span class="tio-view-as-person pii">{{this.personName}}</span>
            {{#if this.roleLabel}}
              <span class="tio-view-as-meta">{{this.roleLabel}}</span>
            {{/if}}
            {{#if this.partnerName}}
              <span class="tio-view-as-meta">{{this.partnerName}}</span>
            {{/if}}
          </div>

          <span class="tio-view-as-mode">{{t "impersonation.mutations_confirmed"}}</span>

          {{#if this.timeRemaining}}
            <span class="tio-view-as-countdown" data-test-impersonation-countdown>
              {{t "impersonation.expires_in" time=this.timeRemaining}}
            </span>
          {{/if}}

          <Button
            @appearance="minimal"
            @size="sm"
            @testHook="view-as-banner__exit"
            class="tio-view-as-exit"
            {{on "click" this.endSession}}
            data-test-impersonation-end-session
          >
            {{t "impersonation.exit"}}
          </Button>
        </div>
      </div>

      <ConfirmationDialog
        @isOpen={{this.impersonation.showMutationConfirm}}
        @title={{t "impersonation.confirm_mutation_title"}}
        @intent="warning"
        @confirmText={{t "impersonation.confirm_proceed"}}
        @cancelText={{t "impersonation.confirm_cancel"}}
        @onConfirm={{this.impersonation.handleConfirm}}
        @onCancel={{this.impersonation.handleCancel}}
      >
        {{t "impersonation.confirm_mutation_body"}}
      </ConfirmationDialog>
    {{/if}}
  </template>
}
